// 붙여넣기 파서 결과를 고객 폼에 합친다 — 비어 있는 칸만 채우고 사람이 친 값은 덮지 않는다(저장은 사람이 확인하고 누른다).

import type { PasteParseResult, PasteSkip } from "./paste-parse";

export interface PasteFormValues {
  name: string;
  phone: string;
  site_address: string;
  area_band: string;
  memo: string;
}

export type PasteFillField = keyof PasteFormValues;

// filled 채움 · kept 이미 친 값이 있어 그대로 둠 · ambiguous 후보가 여러 개라 비워 둠 · no-band 맞는 평형대 선택지가 없어 메모에만 · none 못 찾음.
export type PasteFillStatus =
  "filled" | "kept" | "ambiguous" | "no-band" | "none";

export interface PasteFillRow {
  field: PasteFillField;
  status: PasteFillStatus;
  // 화면에 보일 글자(평형대는 선택지 라벨 + 평수).
  shown?: string;
}

export interface PasteFill {
  patch: Partial<PasteFormValues>;
  rows: PasteFillRow[];
  phoneChoices: string[];
  memoTruncated: boolean;
  // 평수 없이 ㎡ 만 있을 때 — 평형대로 바꾸지 않았다고 알린다.
  areaM2?: number;
  // 찾았지만 칸에 쓰지 않은 번호(대표번호·우리 회사 번호)·주소(우리 회사 주소)와 이유.
  skipped?: PasteSkip[];
}

export interface BandOption {
  value: string;
  label: string;
}

const isBlank = (value: string | undefined): boolean =>
  !value || value.trim() === "";

const formatPyeong = (pyeong: number): string => `${pyeong}평`;

// 평수 → 평형대 선택지 — 내장 코드(BAND_30·BAND_60_PLUS)를 먼저, 없으면 「30평대」 로 시작하는 라벨을 찾는다.
export const areaBandFor = (
  pyeong: number,
  options: readonly BandOption[],
): BandOption | null => {
  if (!Number.isFinite(pyeong) || pyeong < 10) return null;
  const decade = Math.min(Math.floor(pyeong / 10) * 10, 60);
  const code = decade >= 60 ? "BAND_60_PLUS" : `BAND_${decade}`;
  const byCode = options.find((option) => option.value === code);
  if (byCode) return byCode;
  return (
    options.find((option) => option.label.trim().startsWith(`${decade}평대`)) ??
    null
  );
};

const textRow = (
  field: "name" | "phone" | "site_address",
  current: PasteFormValues,
  parsed: PasteParseResult,
  patch: Partial<PasteFormValues>,
): PasteFillRow => {
  const found = parsed[field];
  if (!isBlank(current[field])) return { field, status: "kept", shown: found };
  if (found) {
    patch[field] = found;
    return { field, status: "filled", shown: found };
  }
  return {
    field,
    status: parsed.ambiguous.includes(field) ? "ambiguous" : "none",
  };
};

const bandRow = (
  current: PasteFormValues,
  parsed: PasteParseResult,
  options: readonly BandOption[],
  patch: Partial<PasteFormValues>,
): PasteFillRow => {
  const pyeong = parsed.area_pyeong;
  const decade = pyeong === undefined ? parsed.area_decade : undefined;
  const band = pyeong !== undefined ? areaBandFor(pyeong, options) : decade !== undefined ? areaBandFor(decade, options) : null;
  const shown =
    pyeong !== undefined
      ? band
        ? `${band.label} (${formatPyeong(pyeong)})`
        : formatPyeong(pyeong)
      : decade !== undefined
        ? (band?.label ?? `${decade}평대`)
        : undefined;
  if (!isBlank(current.area_band))
    return { field: "area_band", status: "kept", shown };
  if (pyeong === undefined && decade === undefined) {
    return {
      field: "area_band",
      status: parsed.ambiguous.includes("area_pyeong") ? "ambiguous" : "none",
    };
  }
  if (!band) return { field: "area_band", status: "no-band", shown };
  patch.area_band = band.value;
  return { field: "area_band", status: "filled", shown };
};

export const fillFromPaste = (
  current: PasteFormValues,
  parsed: PasteParseResult,
  bandOptions: readonly BandOption[],
): PasteFill => {
  const patch: Partial<PasteFormValues> = {};
  const rows: PasteFillRow[] = [
    textRow("name", current, parsed, patch),
    textRow("phone", current, parsed, patch),
    textRow("site_address", current, parsed, patch),
    bandRow(current, parsed, bandOptions, patch),
  ];

  const memo = parsed.memo.trim();
  if (!isBlank(current.memo)) rows.push({ field: "memo", status: "kept" });
  else if (memo) {
    patch.memo = parsed.memo;
    rows.push({ field: "memo", status: "filled", shown: "문의 원문" });
  } else rows.push({ field: "memo", status: "none" });

  return {
    patch,
    rows,
    phoneChoices: parsed.phones.length > 1 ? [...parsed.phones] : [],
    memoTruncated: parsed.memo_truncated && patch.memo !== undefined,
    ...(parsed.area_m2 !== undefined && parsed.area_pyeong === undefined
      ? { areaM2: parsed.area_m2 }
      : {}),
    ...(parsed.skipped ? { skipped: parsed.skipped.map((s) => ({ ...s })) } : {}),
  };
};

// 휴대폰 후보 칩 — 사람이 친 번호가 있으면(채우기 전부터 있었거나 후보 밖 번호로 고쳤으면) 덮지 않게 띄우지 않는다.
export const phoneChipsOf = (fill: PasteFill, currentPhone: string): string[] => {
  const row = fill.rows.find((r) => r.field === "phone");
  if (!row || row.status === "kept") return [];
  const now = currentPhone.trim();
  if (now && !fill.phoneChoices.includes(now)) return [];
  return fill.phoneChoices;
};
